import { useMemo, useState, type DragEvent } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/Button";
import { CardTarefa } from "./CardTarefa";
import { CalendarioSemanalGrid } from "./CalendarioSemanalGrid";
import type { TarefaDto, TarefaStatus } from "@/services/tarefas";
import {
  addDaysToKey,
  dayNumber,
  formatWeekRange,
  formatWeekdayShort,
  getFullWeekDays,
  getWeekStartFromKey,
  getWorkWeekDays,
  todayKey,
} from "./dateUtils";
import type { TarefasNavView } from "./TarefasSidebar";
import { splitTarefasAtivasConcluidas } from "./statusTheme";
import { getTarefaDragId } from "./tarefaDrag";

interface QuadroSemanalTarefasProps {
  weekStart: string;
  tarefas: TarefaDto[];
  statusFilter: TarefaStatus | "";
  grupoFilter: string;
  navView: TarefasNavView;
  currentUserId: string;
  busyId?: string | null;
  onWeekChange: (weekStart: string) => void;
  onNovaTarefa: (data: string) => void;
  onEdit: (tarefa: TarefaDto) => void;
  onLeadtime: (tarefa: TarefaDto) => void;
  onStatusChange: (tarefa: TarefaDto, status: TarefaStatus) => void;
  onMoveToDay?: (tarefa: TarefaDto, newDate: string) => void;
}

export function QuadroSemanalTarefas({
  weekStart,
  tarefas,
  statusFilter,
  grupoFilter,
  navView,
  currentUserId,
  busyId,
  onWeekChange,
  onNovaTarefa,
  onEdit,
  onLeadtime,
  onStatusChange,
  onMoveToDay,
}: QuadroSemanalTarefasProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetDay, setDropTargetDay] = useState<string | null>(null);

  const dragEnabled = Boolean(onMoveToDay) && navView !== "hoje";
  const today = todayKey();
  const isCalendario = navView === "calendario";

  const weekDays = useMemo(
    () => (isCalendario ? getFullWeekDays(weekStart) : getWorkWeekDays(weekStart)),
    [weekStart, isCalendario],
  );

  const visibleDays = useMemo(() => {
    if (navView === "hoje") return [today];
    return weekDays;
  }, [navView, today, weekDays]);

  const tarefasByDay = useMemo(() => {
    const map = new Map<string, TarefaDto[]>();
    for (const day of visibleDays) map.set(day, []);

    for (const t of tarefas) {
      if (!map.has(t.data)) continue;
      const tipo = t.tipo ?? "tarefa";
      if (isCalendario ? tipo !== "evento" : tipo === "evento") continue;
      if (statusFilter && t.status !== statusFilter) continue;
      if (grupoFilter && t.grupoId !== grupoFilter) continue;
      map.get(t.data)!.push(t);
    }

    return map;
  }, [tarefas, visibleDays, statusFilter, grupoFilter, isCalendario]);

  const eventosFiltrados = useMemo(() => {
    if (!isCalendario) return [];
    return tarefas.filter((t) => {
      if ((t.tipo ?? "tarefa") !== "evento") return false;
      if (statusFilter && t.status !== statusFilter) return false;
      if (grupoFilter && t.grupoId !== grupoFilter) return false;
      return visibleDays.includes(t.data);
    });
  }, [tarefas, isCalendario, statusFilter, grupoFilter, visibleDays]);

  const tarefaById = useMemo(() => new Map(tarefas.map((t) => [t.id, t])), [tarefas]);

  const handleColumnDragOver = (e: DragEvent, dayKey: string) => {
    if (!dragEnabled || !draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetDay(dayKey);
  };

  const handleColumnDrop = (e: DragEvent, dayKey: string) => {
    e.preventDefault();
    setDropTargetDay(null);
    setDraggingId(null);
    if (!onMoveToDay) return;
    const tarefaId = getTarefaDragId(e.dataTransfer);
    if (!tarefaId) return;
    const tarefa = tarefaById.get(tarefaId);
    if (!tarefa || tarefa.data === dayKey) return;
    onMoveToDay(tarefa, dayKey);
  };

  if (isCalendario) {
    return (
      <CalendarioSemanalGrid
        weekStart={weekStart}
        eventos={eventosFiltrados}
        currentUserId={currentUserId}
        busyId={busyId}
        onWeekChange={onWeekChange}
        onNovaTarefa={onNovaTarefa}
        onEdit={onEdit}
        onLeadtime={onLeadtime}
        onStatusChange={onStatusChange}
      />
    );
  }

  const goPrev = () => onWeekChange(addDaysToKey(weekStart, -7));
  const goNext = () => onWeekChange(addDaysToKey(weekStart, 7));
  const goToday = () => onWeekChange(getWeekStartFromKey(todayKey()));

  const weekLabel = navView === "hoje" ? "Hoje" : formatWeekRange(weekStart);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-1 border-b border-slate-200 px-3 py-2 sm:px-4">
        {navView !== "hoje" && (
          <>
            <Button variant="ghost" size="sm" onClick={goPrev} aria-label="Semana anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goNext} aria-label="Próxima semana">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" onClick={goToday}>
          Hoje
        </Button>
        <h2 className="ml-1 min-w-0 flex-1 truncate text-sm font-medium text-slate-800 sm:ml-2 sm:text-base">
          {weekLabel}
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2 sm:p-3">
        <div
          className={cn(
            "grid h-full min-h-[min(70vh,28rem)] gap-2",
            navView === "hoje"
              ? "mx-auto w-full max-w-lg grid-cols-1"
              : "w-max min-w-full snap-x snap-mandatory auto-cols-[minmax(16.5rem,1fr)] grid-flow-col lg:snap-none lg:w-full lg:min-w-0 lg:auto-cols-auto lg:grid-flow-row lg:grid-cols-5",
          )}
        >
          {visibleDays.map((dayKey) => {
            const dayTasks = tarefasByDay.get(dayKey) ?? [];
            const { ativas, concluidas } = splitTarefasAtivasConcluidas(dayTasks);
            const isToday = dayKey === today;
            const vazio = ativas.length === 0 && concluidas.length === 0;

            const renderCard = (tarefa: TarefaDto) => (
              <CardTarefa
                key={tarefa.id}
                tarefa={tarefa}
                currentUserId={currentUserId}
                busy={busyId === tarefa.id}
                draggable={dragEnabled}
                isDragging={draggingId === tarefa.id}
                onEdit={onEdit}
                onLeadtime={onLeadtime}
                onStatusChange={onStatusChange}
                onDragStart={setDraggingId}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDropTargetDay(null);
                }}
              />
            );

            const isDropTarget = dropTargetDay === dayKey && Boolean(draggingId);

            return (
              <div
                key={dayKey}
                className={cn(
                  "flex h-full min-h-0 snap-start flex-col rounded-xl border bg-slate-50/30 transition-colors",
                  navView !== "hoje" && "min-w-[16.5rem] lg:min-w-0",
                  isToday ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200",
                  isDropTarget && "border-blue-400 bg-blue-50/50 ring-2 ring-blue-200",
                )}
                onDragOver={(e) => handleColumnDragOver(e, dayKey)}
                onDragEnter={(e) => handleColumnDragOver(e, dayKey)}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDropTargetDay((prev) => (prev === dayKey ? null : prev));
                  }
                }}
                onDrop={(e) => handleColumnDrop(e, dayKey)}
              >
                <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase text-slate-500">
                      {formatWeekdayShort(dayKey)}
                    </span>
                    <span
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold",
                        isToday ? "bg-blue-600 text-white" : "text-slate-700",
                      )}
                    >
                      {dayNumber(dayKey)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNovaTarefa(dayKey)}
                    className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-blue-600"
                    aria-label="Adicionar tarefa"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex min-h-0 flex-1 flex-col p-2">
                  {vazio ? (
                    <p
                      className={cn(
                        "flex flex-1 items-center justify-center rounded-lg border border-dashed border-transparent px-2 text-center text-xs text-slate-400",
                        isDropTarget && "border-blue-300 bg-blue-50/40 text-blue-500",
                      )}
                    >
                      {isDropTarget ? "Solte aqui para mover" : "Nenhuma tarefa"}
                    </p>
                  ) : (
                    <>
                      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                        {ativas.map(renderCard)}
                      </div>
                      {concluidas.length > 0 && (
                        <div className="mt-auto flex-shrink-0 space-y-2 border-t border-slate-200 pt-2">
                          {concluidas.map(renderCard)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onNovaTarefa(dayKey)}
                  className="flex-shrink-0 border-t border-slate-100 py-2 text-center text-xs text-slate-400 hover:bg-white hover:text-blue-600"
                >
                  + Adicionar tarefa
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
